/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
 */

import React from 'react'
import PropTypes from 'prop-types'
import autoBind from 'react-autobind'
import qs from 'query-string'
import R from 'ramda'
import { injectIntl } from 'react-intl'

import AutoCompleteField from './AutoCompleteField'
import DefaultSelectionRenderer from './DefaultSelectionRenderer'
import Request from 'generic/request/Request'
import { isDisabled } from './fields'
import * as constants from './constants'
import { red500 } from 'material-ui/styles/colors'

class AutoComplete extends React.PureComponent {
  constructor (props) {
    super(props)

    autoBind(this)

    this.state = {
      source: [],
      searchText: '',
      loading: false,
    }

    this.autocompleteController
    this.requestToken = 0
  }

  componentDidUpdate(prevProps) {
    if (this.props.cleanAutoCompleteSearch &&
        prevProps.cleanAutoCompleteSearch !== this.props.cleanAutoCompleteSearch) {
      this.setState({
        searchText: ''
      })
    }
  }

  loadUrl (searchText, url, parameters) {
    const query = {
      search: searchText ? this.props.searchKey + ':' + searchText : undefined,
      meta: false,
      objects_count: false,
      ...parameters
    }

    return url + '?' + qs.stringify(query)
  }

  performRequest (searchText) {
    return Request.getRequest(
      this.loadUrl(searchText, this.props.url, this.props.parameters),
      {signal: this.autocompleteController.signal},
    )
      .then((results) => {
        if (this.props.transformData) {
          return results.data.objects.objects.map(item => this.props.transformData(item, this.props.name))
        }

        return results.data.objects.objects
      })
  }

  setFloatingLabelText (floatingLabelText, meta) {
    if (floatingLabelText && meta && meta.mandatory) {
      return [floatingLabelText, <span key={0} style={{color: red500, fontSize:20, verticalAlign:'sub'}}> *</span>]
    }

    return floatingLabelText
  }

  handleUpdate (item) {
    this.requestToken += 1

    if (this.autocompleteController) {
      this.autocompleteController.abort()
      this.autocompleteController = null
    }

    if (item) {
      this.setState({
        loading: false
      })
    }

    if (this.props.multi === true) {
      this.setState({
        searchText: '',
        source: []
      })
    }

    if (!item || (item && !item.extraOptionTag)) {
      this.props.updateItem(this.props.name, item)
    } else if (this.props.extraAction) {
      this.props.extraAction(item)
    }

    if (item && this.props.formatDisplay) {
      this.props.formatDisplay(item)
    }
  }

  handleSearch (searchText, dataSource, params) {
    let loading = true
    let source = []
    const currentRequestToken = ++this.requestToken

    this.setState({
      loading: loading,
      searchText: searchText
    })


    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }


    if (searchText === '') {
      this.setState({
        loading: false,
        searchText: '',
        source: []
      })
    }

    if (this.props.onUpdateInput) {
      this.props.onUpdateInput(searchText, dataSource, params)
    }

    this.searchTimeout = setTimeout(() => {
      if (this.autocompleteController) {
        this.autocompleteController.abort()
      }

      this.autocompleteController = new window.AbortController()

      this.performRequest(searchText)
        .then((results) => {
          source = results
          loading = false
        })
        .catch((error) => {
          if (!R.path(['message'], error) === "canceled") {
            loading = false
            throw error
          }
        })
        .finally(() => {
          if (currentRequestToken === this.requestToken) {
            this.setState({
              loading: loading,
              source: source
            })
          }
        })

        this.searchTimeout = null
    }, 300)
  }

  computeDataSource (source) {
    if (!this.props.extraOption || (!this.props.searchText && !this.state.searchText)) {
      return source
    }

    return [...source, {
      [this.props.textKey]: this.props.searchText || this.state.searchText,
      [this.props.valueKey]: this.props.extraOption,
      extraOptionTag: true
    }]
  }

  render () {
    const {
      floatingLabelText,
      meta,
      onUpdateInput,
      textKey,
      valueKey,
      searchKey,
      updateItem,
      multi,
      deleteValue,
      extraAction,
      extraOption,
      placeholderMandatory,
      transformData,
      ...rest
    } = this.props

    const SelectionRenderer = this.props.selectionRenderer || DefaultSelectionRenderer

    let componentProps = {
      disabled: isDisabled(meta),
      floatingLabelText: this.setFloatingLabelText(floatingLabelText, meta),
      ...rest
    }

    return (
      <div style={{position: 'relative'}}>
        <AutoCompleteField
          popoverProps={{style: {width: 'auto', minWidth: 400}}}
          name={this.props.name}
          className='autocomplete'
          listStyle={{width: '100%', maxHeight: 200, overflow: 'auto'}}
          searchText={this.props.searchText || this.state.searchText}
          loading={this.state.loading}
          loadingBottom={15}
          openOnFocus={true}
          fullWidth={true}
          dataSource={
            !this.state.loading
             ? this.computeDataSource(this.state.source)
             : []
          }
          dataSourceConfig={{text: this.props.textKey, value: this.props.valueKey}}
          onUpdateInput={this.handleSearch}
          onNewRequest={this.handleUpdate}
          errorStyle={{fontSize:10}}
          placeholder={
            placeholderMandatory
              ? this.props.intl.formatMessage(constants.messages.MESSAGE_MANDATORY)
              : this.props.placeholder
          }
          {
          ...componentProps
          }
        />

        {
          multi
            ? <SelectionRenderer
              name={this.props.name}
              values={this.props.values}
              deleteValue={this.props.deleteValue}
              textKey={this.props.selectionKey || this.props.textKey}
              valueKey={this.props.valueKey}
            />
            : null
        }
        {
          meta &&
          meta.mandatory &&
          !this.props.searchText &&
          !placeholderMandatory &&
          !this.props.errorText
            ? <p className={'error-message'} style={{margin: 0}}>
              {this.props.intl.formatMessage(constants.messages.MESSAGE_MANDATORY)}
            </p>
            : null
        }
      </div>
    )
  }
}

AutoComplete.propTypes = {
  url: PropTypes.string.isRequired,
  parameters: PropTypes.object,
  transformData: PropTypes.func,
  updateItem: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  floatingLabelText: PropTypes.string,
  searchText: PropTypes.string,
  textKey: PropTypes.string,
  valueKey: PropTypes.string,
  searchKey: PropTypes.string,
  errorText: PropTypes.string,
  meta: PropTypes.object,
  disabled: PropTypes.any,
  multi: PropTypes.bool,
  selectionRenderer: PropTypes.any,
  values: PropTypes.array,
  deleteValue: PropTypes.func,
  extraOption: PropTypes.any,
  extraAction: PropTypes.func
}

export default injectIntl(AutoComplete)



// WEBPACK FOOTER //
// ./src/generic/form/AutoComplete.js